// Reused from EightbotsJ/loop, deployed release ea578de; see docs/commerce-source-provenance.md.
import { z } from "zod";

export const chainFamilies = ["utxo", "evm", "tron", "solana"] as const;
export const chainFamilySchema = z.enum(chainFamilies);
export type ChainFamily = z.infer<typeof chainFamilySchema>;

export const protocolProfiles = [
  "bitcoin-core-descriptor-psbt-v1",
  "litecoin-core-legacy-psbt-v1",
  "dogecoin-core-legacy-raw-v1",
  "evm-eip1559-native-v1",
  "tron-native-transfer-v1",
  "solana-system-transfer-legacy-v1",
] as const;
export const protocolProfileSchema = z.enum(protocolProfiles);
export type ProtocolProfile = z.infer<typeof protocolProfileSchema>;

export const addressCodecs = [
  "bitcoin-bech32-mainnet",
  "bitcoin-bech32-testnet",
  "bitcoin-bech32-regtest",
  "litecoin-bech32-p2wpkh-mainnet",
  "dogecoin-base58-p2pkh-mainnet",
  "evm-hex20",
  "tron-base58check-mainnet",
  "solana-base58-pubkey",
] as const;
export const addressCodecSchema = z.enum(addressCodecs);
export type AddressCodec = z.infer<typeof addressCodecSchema>;

export const transactionIdCodecs = [
  "hex64-lower",
  "hex0x64-lower",
  "base58-case-sensitive",
] as const;
export const transactionIdCodecSchema = z.enum(transactionIdCodecs);
export type TransactionIdCodec = z.infer<typeof transactionIdCodecSchema>;

export const blockIdCodecs = [
  "hex64-lower",
  "hex0x64-lower",
  "tron-block-id-hex64",
  "base58-case-sensitive",
] as const;
export const blockIdCodecSchema = z.enum(blockIdCodecs);
export type BlockIdCodec = z.infer<typeof blockIdCodecSchema>;

export const scanUnits = ["height", "slot"] as const;
export const scanUnitSchema = z.enum(scanUnits);
export type ScanUnit = z.infer<typeof scanUnitSchema>;

export const eventIdentityKinds = [
  "utxo-outpoint",
  "evm-transfer-index",
  "tron-contract-index",
  "solana-instruction-path",
] as const;
export const eventIdentityKindSchema = z.enum(eventIdentityKinds);
export type EventIdentityKind = z.infer<typeof eventIdentityKindSchema>;

export const feeModels = [
  "sat-vbyte",
  "atomic-kbyte",
  "wei-gas",
  "tron-resources",
  "solana-lamports",
] as const;
export const feeModelSchema = z.enum(feeModels);
export type FeeModel = z.infer<typeof feeModelSchema>;

export const finalityModels = [
  "confirmation-depth",
  "evm-finalized",
  "tron-solidified",
  "solana-finalized",
] as const;
export const finalityModelSchema = z.enum(finalityModels);
export type FinalityModel = z.infer<typeof finalityModelSchema>;

export const validityModels = ["none", "timestamp-ms", "block-height"] as const;
export const validityModelSchema = z.enum(validityModels);
export type ValidityModel = z.infer<typeof validityModelSchema>;

export const unsignedTransactionFormats = [
  "bitcoin-psbt",
  "litecoin-psbt",
  "dogecoin-raw-envelope",
  "evm-eip1559",
  "tron-protobuf-raw-data",
  "solana-legacy-message",
] as const;
export const unsignedTransactionFormatSchema = z.enum(unsignedTransactionFormats);
export type UnsignedTransactionFormat = z.infer<typeof unsignedTransactionFormatSchema>;

export const signedTransactionFormats = [
  "utxo-raw",
  "evm-eip1559",
  "tron-protobuf-transaction",
  "solana-wire-transaction",
] as const;
export const signedTransactionFormatSchema = z.enum(signedTransactionFormats);
export type SignedTransactionFormat = z.infer<typeof signedTransactionFormatSchema>;

const networkEnvironmentSchema = z.enum(["development", "testnet", "mainnet"]);
const nativeAssetDefinitionSchema = z.object({
  code: z.string().regex(/^[A-Za-z][A-Za-z0-9]{1,9}$/u),
  decimals: z.union([z.literal(6), z.literal(8), z.literal(9), z.literal(18)]),
  atomicUnit: z.enum(["satoshi", "litoshi", "koinu", "wei", "sun", "lamport"]),
}).strict();

const networkIdentitySchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("utxo-genesis"),
    genesisHash: z.string().regex(/^[0-9a-f]{64}$/u),
    rpcChain: z.enum(["main", "test", "regtest"]),
    defaultRpcPort: z.number().int().positive().max(65535).optional(),
    nodeVersion: z.number().int().positive().safe().optional(),
    mwebActivationHeight: z.string().regex(/^[1-9][0-9]*$/u).optional(),
  }).strict(),
  z.object({
    kind: z.literal("evm-chain-id"),
    chainIdDecimal: z.string().regex(/^[1-9][0-9]*$/u),
  }).strict(),
  z.object({
    kind: z.literal("tron-mainnet"),
    ethChainIdHex: z.literal("0x2b6653dc"),
    addressVersionHex: z.literal("41"),
  }).strict(),
  z.object({
    kind: z.literal("solana-genesis"),
    genesisHash: z.string().regex(/^[1-9A-HJ-NP-Za-km-z]{32,64}$/u),
  }).strict(),
]);

const addressDefinitionSchema = z.object({
  codec: addressCodecSchema,
  destinationValidation: z.enum([
    "syntax-and-checksum",
    "evm-hex20",
    "solana-system-or-unfunded-on-curve",
  ]),
}).strict();

const transactionDefinitionSchema = z.object({
  unsignedFormat: unsignedTransactionFormatSchema,
  signedFormat: signedTransactionFormatSchema,
  transactionIdCodec: transactionIdCodecSchema,
  blockIdCodec: blockIdCodecSchema,
  eventIdentity: eventIdentityKindSchema,
  validity: validityModelSchema,
}).strict();

const finalityDefinitionSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("confirmation-depth"),
    display: z.literal("confirmations"),
    confirmationPolicy: z.literal("wallet-network-settings"),
  }).strict(),
  z.object({
    kind: z.literal("evm-finalized"),
    display: z.literal("finalized"),
    confirmationPolicy: z.literal("wallet-network-settings"),
  }).strict(),
  z.object({
    kind: z.literal("tron-solidified"),
    display: z.literal("solidified"),
    confirmationPolicy: z.literal("protocol"),
  }).strict(),
  z.object({
    kind: z.literal("solana-finalized"),
    display: z.literal("finalized"),
    confirmationPolicy: z.literal("protocol"),
  }).strict(),
]);

const protocolDescriptorBaseSchema = z.object({
  displayName: z.string().min(1).max(80),
  environment: networkEnvironmentSchema,
  family: chainFamilySchema,
  profile: protocolProfileSchema,
  nativeAsset: nativeAssetDefinitionSchema,
  identity: networkIdentitySchema,
  address: addressDefinitionSchema,
  transaction: transactionDefinitionSchema,
  rpcProfile: z.enum([
    "bitcoin-core",
    "litecoin-core",
    "dogecoin-core",
    "evm-json-rpc",
    "tron-full-solidity-http",
    "solana-json-rpc",
  ]),
  scanUnit: scanUnitSchema,
  feeModel: feeModelSchema,
  finality: finalityDefinitionSchema,
  tokenStandard: z.enum(["erc20"]).nullable(),
}).strict();

type ProfileRule = Readonly<{
  family: ChainFamily;
  identityKind: z.infer<typeof networkIdentitySchema>["kind"];
  rpcProfile: z.infer<typeof protocolDescriptorBaseSchema>["rpcProfile"];
  scanUnit: ScanUnit;
  feeModel: FeeModel;
  finalityModel: FinalityModel;
  unsignedFormat: UnsignedTransactionFormat;
  signedFormat: SignedTransactionFormat;
  eventIdentity: EventIdentityKind;
  validity: ValidityModel;
  tokenStandard: "erc20" | null;
}>;

const profileRules = Object.freeze({
  "bitcoin-core-descriptor-psbt-v1": {
    family: "utxo", identityKind: "utxo-genesis", rpcProfile: "bitcoin-core", scanUnit: "height", feeModel: "sat-vbyte",
    finalityModel: "confirmation-depth", unsignedFormat: "bitcoin-psbt", signedFormat: "utxo-raw",
    eventIdentity: "utxo-outpoint", validity: "none", tokenStandard: null,
  },
  "litecoin-core-legacy-psbt-v1": {
    family: "utxo", identityKind: "utxo-genesis", rpcProfile: "litecoin-core", scanUnit: "height", feeModel: "sat-vbyte",
    finalityModel: "confirmation-depth", unsignedFormat: "litecoin-psbt", signedFormat: "utxo-raw",
    eventIdentity: "utxo-outpoint", validity: "none", tokenStandard: null,
  },
  "dogecoin-core-legacy-raw-v1": {
    family: "utxo", identityKind: "utxo-genesis", rpcProfile: "dogecoin-core", scanUnit: "height", feeModel: "atomic-kbyte",
    finalityModel: "confirmation-depth", unsignedFormat: "dogecoin-raw-envelope", signedFormat: "utxo-raw",
    eventIdentity: "utxo-outpoint", validity: "none", tokenStandard: null,
  },
  "evm-eip1559-native-v1": {
    family: "evm", identityKind: "evm-chain-id", rpcProfile: "evm-json-rpc", scanUnit: "height", feeModel: "wei-gas",
    finalityModel: "evm-finalized", unsignedFormat: "evm-eip1559", signedFormat: "evm-eip1559",
    eventIdentity: "evm-transfer-index", validity: "none", tokenStandard: "erc20",
  },
  "tron-native-transfer-v1": {
    family: "tron", identityKind: "tron-mainnet", rpcProfile: "tron-full-solidity-http", scanUnit: "height", feeModel: "tron-resources",
    finalityModel: "tron-solidified", unsignedFormat: "tron-protobuf-raw-data", signedFormat: "tron-protobuf-transaction",
    eventIdentity: "tron-contract-index", validity: "timestamp-ms", tokenStandard: null,
  },
  "solana-system-transfer-legacy-v1": {
    family: "solana", identityKind: "solana-genesis", rpcProfile: "solana-json-rpc", scanUnit: "slot", feeModel: "solana-lamports",
    finalityModel: "solana-finalized", unsignedFormat: "solana-legacy-message", signedFormat: "solana-wire-transaction",
    eventIdentity: "solana-instruction-path", validity: "block-height", tokenStandard: null,
  },
} satisfies Readonly<Record<ProtocolProfile, ProfileRule>>);

export const protocolDescriptorSchema = protocolDescriptorBaseSchema.superRefine((descriptor, context) => {
  const rule = profileRules[descriptor.profile];
  const mwebActivationConfigured = descriptor.identity.kind === "utxo-genesis"
    && descriptor.identity.mwebActivationHeight !== undefined;
  if (mwebActivationConfigured !== (descriptor.profile === "litecoin-core-legacy-psbt-v1")) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ["identity", "mwebActivationHeight"], message: "Only the Litecoin MWEB profile must own an activation height" });
  }
  const nativeCoreRpcPortConfigured = descriptor.identity.kind === "utxo-genesis"
    && descriptor.identity.defaultRpcPort !== undefined;
  const nativeCoreNodeVersionConfigured = descriptor.identity.kind === "utxo-genesis"
    && descriptor.identity.nodeVersion !== undefined;
  const nativeCoreRequired = descriptor.profile === "litecoin-core-legacy-psbt-v1" || descriptor.profile === "dogecoin-core-legacy-raw-v1";
  const nativeCoreOptional = descriptor.profile === "bitcoin-core-descriptor-psbt-v1";
  const nativeCoreFieldsAgree = nativeCoreRpcPortConfigured === nativeCoreNodeVersionConfigured;
  if (!nativeCoreFieldsAgree || nativeCoreRequired && !nativeCoreRpcPortConfigured || nativeCoreRpcPortConfigured && !nativeCoreRequired && !nativeCoreOptional) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ["identity"], message: "A local native Core configuration must own its RPC port and node version together" });
  }
  const comparisons: readonly [readonly (string | number)[], unknown, unknown][] = [
    [["family"], descriptor.family, rule.family],
    [["identity", "kind"], descriptor.identity.kind, rule.identityKind],
    [["rpcProfile"], descriptor.rpcProfile, rule.rpcProfile],
    [["scanUnit"], descriptor.scanUnit, rule.scanUnit],
    [["feeModel"], descriptor.feeModel, rule.feeModel],
    [["finality", "kind"], descriptor.finality.kind, rule.finalityModel],
    [["transaction", "unsignedFormat"], descriptor.transaction.unsignedFormat, rule.unsignedFormat],
    [["transaction", "signedFormat"], descriptor.transaction.signedFormat, rule.signedFormat],
    [["transaction", "eventIdentity"], descriptor.transaction.eventIdentity, rule.eventIdentity],
    [["transaction", "validity"], descriptor.transaction.validity, rule.validity],
    [["tokenStandard"], descriptor.tokenStandard, rule.tokenStandard],
  ];
  for (const [path, actual, expected] of comparisons) {
    if (actual !== expected) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: [...path], message: "The protocol profile fields do not agree" });
    }
  }
});
export type ProtocolDescriptor = z.infer<typeof protocolDescriptorSchema>;

export const nativeCoreRuntimeProfileSchema = z.object({
  daemonExecutable: z.string().regex(/^[a-z][a-z0-9-]*$/u),
  cliExecutable: z.string().regex(/^[a-z][a-z0-9-]*$/u),
  versionProduct: z.string().regex(/^[A-Z][A-Za-z0-9 ]*$/u),
  imageProduct: z.string().regex(/^[a-z][a-z0-9-]*$/u),
}).strict();
export type NativeCoreRuntimeProfile = z.infer<typeof nativeCoreRuntimeProfileSchema>;

const nativeCoreRuntimeProfileSource = {
  "bitcoin-core-descriptor-psbt-v1": {
    daemonExecutable: "bitcoind", cliExecutable: "bitcoin-cli", versionProduct: "Bitcoin", imageProduct: "bitcoin",
  },
  "litecoin-core-legacy-psbt-v1": {
    daemonExecutable: "litecoind", cliExecutable: "litecoin-cli", versionProduct: "Litecoin", imageProduct: "litecoin",
  },
  "dogecoin-core-legacy-raw-v1": {
    daemonExecutable: "dogecoind", cliExecutable: "dogecoin-cli", versionProduct: "Dogecoin", imageProduct: "dogecoin",
  },
} as const satisfies Readonly<Partial<Record<ProtocolProfile, NativeCoreRuntimeProfile>>>;

const nativeCoreProtocolProfileIds = Object.keys(nativeCoreRuntimeProfileSource) as (keyof typeof nativeCoreRuntimeProfileSource)[];
const expectedNativeCoreProtocolProfileIds = protocolProfiles.filter((profile) => {
  const rule = profileRules[profile];
  return rule.family === "utxo" && rule.identityKind === "utxo-genesis";
});
if (nativeCoreProtocolProfileIds.join(",") !== expectedNativeCoreProtocolProfileIds.join(",")) {
  throw new Error("CHAIN_NATIVE_CORE_RUNTIME_PROFILE_CATALOG_INVALID");
}
for (const profile of nativeCoreProtocolProfileIds) {
  nativeCoreRuntimeProfileSchema.parse(nativeCoreRuntimeProfileSource[profile]);
}
export const nativeCoreRuntimeProfiles = deepFreeze(nativeCoreRuntimeProfileSource);
export type NativeCoreProtocolProfile = keyof typeof nativeCoreRuntimeProfiles;

const registrySource = {
  "bitcoin-regtest": {
    displayName: "Bitcoin Regtest", environment: "development", family: "utxo", profile: "bitcoin-core-descriptor-psbt-v1",
    nativeAsset: { code: "tBTC", decimals: 8, atomicUnit: "satoshi" },
    identity: { kind: "utxo-genesis", genesisHash: "0f9188f13cb7b2c71f2a335e3a4fc328bf5beb436012afca590b1a11466e2206", rpcChain: "regtest" },
    address: { codec: "bitcoin-bech32-regtest", destinationValidation: "syntax-and-checksum" },
    transaction: { unsignedFormat: "bitcoin-psbt", signedFormat: "utxo-raw", transactionIdCodec: "hex64-lower", blockIdCodec: "hex64-lower", eventIdentity: "utxo-outpoint", validity: "none" },
    rpcProfile: "bitcoin-core", scanUnit: "height", feeModel: "sat-vbyte",
    finality: { kind: "confirmation-depth", display: "confirmations", confirmationPolicy: "wallet-network-settings" }, tokenStandard: null,
  },
  "bitcoin-testnet": {
    displayName: "Bitcoin Testnet", environment: "testnet", family: "utxo", profile: "bitcoin-core-descriptor-psbt-v1",
    nativeAsset: { code: "tBTC", decimals: 8, atomicUnit: "satoshi" },
    identity: { kind: "utxo-genesis", genesisHash: "000000000933ea01ad0ee984209779baaec3ced90fa3f408719526f8d77f4943", rpcChain: "test" },
    address: { codec: "bitcoin-bech32-testnet", destinationValidation: "syntax-and-checksum" },
    transaction: { unsignedFormat: "bitcoin-psbt", signedFormat: "utxo-raw", transactionIdCodec: "hex64-lower", blockIdCodec: "hex64-lower", eventIdentity: "utxo-outpoint", validity: "none" },
    rpcProfile: "bitcoin-core", scanUnit: "height", feeModel: "sat-vbyte",
    finality: { kind: "confirmation-depth", display: "confirmations", confirmationPolicy: "wallet-network-settings" }, tokenStandard: null,
  },
  "ethereum-sepolia": {
    displayName: "Ethereum Sepolia", environment: "testnet", family: "evm", profile: "evm-eip1559-native-v1",
    nativeAsset: { code: "ETH", decimals: 18, atomicUnit: "wei" },
    identity: { kind: "evm-chain-id", chainIdDecimal: "11155111" },
    address: { codec: "evm-hex20", destinationValidation: "evm-hex20" },
    transaction: { unsignedFormat: "evm-eip1559", signedFormat: "evm-eip1559", transactionIdCodec: "hex0x64-lower", blockIdCodec: "hex0x64-lower", eventIdentity: "evm-transfer-index", validity: "none" },
    rpcProfile: "evm-json-rpc", scanUnit: "height", feeModel: "wei-gas",
    finality: { kind: "evm-finalized", display: "finalized", confirmationPolicy: "wallet-network-settings" }, tokenStandard: "erc20",
  },
  "robinhood-testnet": {
    displayName: "Robinhood Chain Testnet", environment: "testnet", family: "evm", profile: "evm-eip1559-native-v1",
    nativeAsset: { code: "ETH", decimals: 18, atomicUnit: "wei" },
    identity: { kind: "evm-chain-id", chainIdDecimal: "46630" },
    address: { codec: "evm-hex20", destinationValidation: "evm-hex20" },
    transaction: { unsignedFormat: "evm-eip1559", signedFormat: "evm-eip1559", transactionIdCodec: "hex0x64-lower", blockIdCodec: "hex0x64-lower", eventIdentity: "evm-transfer-index", validity: "none" },
    rpcProfile: "evm-json-rpc", scanUnit: "height", feeModel: "wei-gas",
    finality: { kind: "evm-finalized", display: "finalized", confirmationPolicy: "wallet-network-settings" }, tokenStandard: "erc20",
  },
  "bitcoin-mainnet": {
    displayName: "Bitcoin", environment: "mainnet", family: "utxo", profile: "bitcoin-core-descriptor-psbt-v1",
    nativeAsset: { code: "BTC", decimals: 8, atomicUnit: "satoshi" },
    identity: { kind: "utxo-genesis", genesisHash: "000000000019d6689c085ae165831e934ff763ae46a2a6c172b3f1b60a8ce26f", rpcChain: "main", defaultRpcPort: 8332, nodeVersion: 310100 },
    address: { codec: "bitcoin-bech32-mainnet", destinationValidation: "syntax-and-checksum" },
    transaction: { unsignedFormat: "bitcoin-psbt", signedFormat: "utxo-raw", transactionIdCodec: "hex64-lower", blockIdCodec: "hex64-lower", eventIdentity: "utxo-outpoint", validity: "none" },
    rpcProfile: "bitcoin-core", scanUnit: "height", feeModel: "sat-vbyte",
    finality: { kind: "confirmation-depth", display: "confirmations", confirmationPolicy: "wallet-network-settings" }, tokenStandard: null,
  },
  "ethereum-mainnet": {
    displayName: "Ethereum", environment: "mainnet", family: "evm", profile: "evm-eip1559-native-v1",
    nativeAsset: { code: "ETH", decimals: 18, atomicUnit: "wei" },
    identity: { kind: "evm-chain-id", chainIdDecimal: "1" },
    address: { codec: "evm-hex20", destinationValidation: "evm-hex20" },
    transaction: { unsignedFormat: "evm-eip1559", signedFormat: "evm-eip1559", transactionIdCodec: "hex0x64-lower", blockIdCodec: "hex0x64-lower", eventIdentity: "evm-transfer-index", validity: "none" },
    rpcProfile: "evm-json-rpc", scanUnit: "height", feeModel: "wei-gas",
    finality: { kind: "evm-finalized", display: "finalized", confirmationPolicy: "wallet-network-settings" }, tokenStandard: "erc20",
  },
  "robinhood-mainnet": {
    displayName: "Robinhood Chain", environment: "mainnet", family: "evm", profile: "evm-eip1559-native-v1",
    nativeAsset: { code: "ETH", decimals: 18, atomicUnit: "wei" },
    identity: { kind: "evm-chain-id", chainIdDecimal: "4663" },
    address: { codec: "evm-hex20", destinationValidation: "evm-hex20" },
    transaction: { unsignedFormat: "evm-eip1559", signedFormat: "evm-eip1559", transactionIdCodec: "hex0x64-lower", blockIdCodec: "hex0x64-lower", eventIdentity: "evm-transfer-index", validity: "none" },
    rpcProfile: "evm-json-rpc", scanUnit: "height", feeModel: "wei-gas",
    finality: { kind: "evm-finalized", display: "finalized", confirmationPolicy: "wallet-network-settings" }, tokenStandard: "erc20",
  },
  "rise-mainnet": {
    displayName: "RISE", environment: "mainnet", family: "evm", profile: "evm-eip1559-native-v1",
    nativeAsset: { code: "ETH", decimals: 18, atomicUnit: "wei" },
    identity: { kind: "evm-chain-id", chainIdDecimal: "4153" },
    address: { codec: "evm-hex20", destinationValidation: "evm-hex20" },
    transaction: { unsignedFormat: "evm-eip1559", signedFormat: "evm-eip1559", transactionIdCodec: "hex0x64-lower", blockIdCodec: "hex0x64-lower", eventIdentity: "evm-transfer-index", validity: "none" },
    rpcProfile: "evm-json-rpc", scanUnit: "height", feeModel: "wei-gas",
    finality: { kind: "evm-finalized", display: "finalized", confirmationPolicy: "wallet-network-settings" }, tokenStandard: "erc20",
  },
  "bnb-mainnet": {
    displayName: "BNB Smart Chain", environment: "mainnet", family: "evm", profile: "evm-eip1559-native-v1",
    nativeAsset: { code: "BNB", decimals: 18, atomicUnit: "wei" },
    identity: { kind: "evm-chain-id", chainIdDecimal: "56" },
    address: { codec: "evm-hex20", destinationValidation: "evm-hex20" },
    transaction: { unsignedFormat: "evm-eip1559", signedFormat: "evm-eip1559", transactionIdCodec: "hex0x64-lower", blockIdCodec: "hex0x64-lower", eventIdentity: "evm-transfer-index", validity: "none" },
    rpcProfile: "evm-json-rpc", scanUnit: "height", feeModel: "wei-gas",
    finality: { kind: "evm-finalized", display: "finalized", confirmationPolicy: "wallet-network-settings" }, tokenStandard: "erc20",
  },
  "hyperevm-mainnet": {
    displayName: "HyperEVM", environment: "mainnet", family: "evm", profile: "evm-eip1559-native-v1",
    nativeAsset: { code: "HYPE", decimals: 18, atomicUnit: "wei" },
    identity: { kind: "evm-chain-id", chainIdDecimal: "999" },
    address: { codec: "evm-hex20", destinationValidation: "evm-hex20" },
    transaction: { unsignedFormat: "evm-eip1559", signedFormat: "evm-eip1559", transactionIdCodec: "hex0x64-lower", blockIdCodec: "hex0x64-lower", eventIdentity: "evm-transfer-index", validity: "none" },
    rpcProfile: "evm-json-rpc", scanUnit: "height", feeModel: "wei-gas",
    finality: { kind: "evm-finalized", display: "finalized", confirmationPolicy: "wallet-network-settings" }, tokenStandard: "erc20",
  },
  "avalanche-mainnet": {
    displayName: "Avalanche C-Chain", environment: "mainnet", family: "evm", profile: "evm-eip1559-native-v1",
    nativeAsset: { code: "AVAX", decimals: 18, atomicUnit: "wei" },
    identity: { kind: "evm-chain-id", chainIdDecimal: "43114" },
    address: { codec: "evm-hex20", destinationValidation: "evm-hex20" },
    transaction: { unsignedFormat: "evm-eip1559", signedFormat: "evm-eip1559", transactionIdCodec: "hex0x64-lower", blockIdCodec: "hex0x64-lower", eventIdentity: "evm-transfer-index", validity: "none" },
    rpcProfile: "evm-json-rpc", scanUnit: "height", feeModel: "wei-gas",
    finality: { kind: "evm-finalized", display: "finalized", confirmationPolicy: "wallet-network-settings" }, tokenStandard: "erc20",
  },
  "cronos-mainnet": {
    displayName: "Cronos EVM", environment: "mainnet", family: "evm", profile: "evm-eip1559-native-v1",
    nativeAsset: { code: "CRO", decimals: 18, atomicUnit: "wei" },
    identity: { kind: "evm-chain-id", chainIdDecimal: "25" },
    address: { codec: "evm-hex20", destinationValidation: "evm-hex20" },
    transaction: { unsignedFormat: "evm-eip1559", signedFormat: "evm-eip1559", transactionIdCodec: "hex0x64-lower", blockIdCodec: "hex0x64-lower", eventIdentity: "evm-transfer-index", validity: "none" },
    rpcProfile: "evm-json-rpc", scanUnit: "height", feeModel: "wei-gas",
    finality: { kind: "evm-finalized", display: "finalized", confirmationPolicy: "wallet-network-settings" }, tokenStandard: "erc20",
  },
  "litecoin-mainnet": {
    displayName: "Litecoin", environment: "mainnet", family: "utxo", profile: "litecoin-core-legacy-psbt-v1",
    nativeAsset: { code: "LTC", decimals: 8, atomicUnit: "litoshi" },
    // MWEB locked in at 2,257,920 and activates one 8,064-block mainnet
    // confirmation window later. Keep this consensus boundary in the registry.
    // https://litecoin.com/news/mweb-has-officially-activated
    // https://github.com/litecoin-project/litecoin/blob/v0.21.5.6/src/chainparams.cpp
    identity: { kind: "utxo-genesis", genesisHash: "12a765e31ffd4059bada1e25190f6e98c99d9714d334efa41a195a7e7e04bfe2", rpcChain: "main", defaultRpcPort: 9332, nodeVersion: 210506, mwebActivationHeight: "2265984" },
    address: { codec: "litecoin-bech32-p2wpkh-mainnet", destinationValidation: "syntax-and-checksum" },
    transaction: { unsignedFormat: "litecoin-psbt", signedFormat: "utxo-raw", transactionIdCodec: "hex64-lower", blockIdCodec: "hex64-lower", eventIdentity: "utxo-outpoint", validity: "none" },
    rpcProfile: "litecoin-core", scanUnit: "height", feeModel: "sat-vbyte",
    finality: { kind: "confirmation-depth", display: "confirmations", confirmationPolicy: "wallet-network-settings" }, tokenStandard: null,
  },
  "dogecoin-mainnet": {
    displayName: "Dogecoin", environment: "mainnet", family: "utxo", profile: "dogecoin-core-legacy-raw-v1",
    nativeAsset: { code: "DOGE", decimals: 8, atomicUnit: "koinu" },
    identity: { kind: "utxo-genesis", genesisHash: "1a91e3dace36e2be3bf030a65679fe821aa1d6ef92e7c9902eb318182c355691", rpcChain: "main", defaultRpcPort: 22555, nodeVersion: 1140900 },
    address: { codec: "dogecoin-base58-p2pkh-mainnet", destinationValidation: "syntax-and-checksum" },
    transaction: { unsignedFormat: "dogecoin-raw-envelope", signedFormat: "utxo-raw", transactionIdCodec: "hex64-lower", blockIdCodec: "hex64-lower", eventIdentity: "utxo-outpoint", validity: "none" },
    rpcProfile: "dogecoin-core", scanUnit: "height", feeModel: "atomic-kbyte",
    finality: { kind: "confirmation-depth", display: "confirmations", confirmationPolicy: "wallet-network-settings" }, tokenStandard: null,
  },
  "tron-mainnet": {
    displayName: "TRON", environment: "mainnet", family: "tron", profile: "tron-native-transfer-v1",
    nativeAsset: { code: "TRX", decimals: 6, atomicUnit: "sun" },
    identity: { kind: "tron-mainnet", ethChainIdHex: "0x2b6653dc", addressVersionHex: "41" },
    address: { codec: "tron-base58check-mainnet", destinationValidation: "syntax-and-checksum" },
    transaction: { unsignedFormat: "tron-protobuf-raw-data", signedFormat: "tron-protobuf-transaction", transactionIdCodec: "hex64-lower", blockIdCodec: "tron-block-id-hex64", eventIdentity: "tron-contract-index", validity: "timestamp-ms" },
    rpcProfile: "tron-full-solidity-http", scanUnit: "height", feeModel: "tron-resources",
    finality: { kind: "tron-solidified", display: "solidified", confirmationPolicy: "protocol" }, tokenStandard: null,
  },
  "solana-mainnet": {
    displayName: "Solana", environment: "mainnet", family: "solana", profile: "solana-system-transfer-legacy-v1",
    nativeAsset: { code: "SOL", decimals: 9, atomicUnit: "lamport" },
    identity: { kind: "solana-genesis", genesisHash: "5eykt4UsFv8P8NJdTREpY1vzqKqZKvdpKuc147dw2N9d" },
    address: { codec: "solana-base58-pubkey", destinationValidation: "solana-system-or-unfunded-on-curve" },
    transaction: { unsignedFormat: "solana-legacy-message", signedFormat: "solana-wire-transaction", transactionIdCodec: "base58-case-sensitive", blockIdCodec: "base58-case-sensitive", eventIdentity: "solana-instruction-path", validity: "block-height" },
    rpcProfile: "solana-json-rpc", scanUnit: "slot", feeModel: "solana-lamports",
    finality: { kind: "solana-finalized", display: "finalized", confirmationPolicy: "protocol" }, tokenStandard: null,
  },
} as const satisfies Readonly<Record<string, ProtocolDescriptor>>;

export const chainProtocolRegistrySchema = z.record(
  z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)+$/u),
  protocolDescriptorSchema,
).superRefine((registry, context) => {
  if (Object.keys(registry).length === 0) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: "The protocol registry cannot be empty" });
  }
  const decimals = new Map<string, number>();
  for (const [network, descriptor] of Object.entries(registry)) {
    if ((descriptor.environment === "mainnet") !== network.endsWith("-mainnet")) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: [network, "environment"], message: "The network ID and environment do not agree" });
    }
    const prior = decimals.get(descriptor.nativeAsset.code);
    if (prior !== undefined && prior !== descriptor.nativeAsset.decimals) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: [network, "nativeAsset", "decimals"], message: "A native asset must have one decimal scale" });
    }
    decimals.set(descriptor.nativeAsset.code, descriptor.nativeAsset.decimals);
  }
});

chainProtocolRegistrySchema.parse(registrySource);
export const chainProtocolRegistry = deepFreeze(registrySource);
export type ChainProtocolRegistry = typeof chainProtocolRegistry;
export type Network = keyof ChainProtocolRegistry;
export type NetworkByFamily<F extends ChainFamily> = {
  [N in Network]: ChainProtocolRegistry[N]["family"] extends F ? N : never
}[Network];
export type NetworkByProfile<P extends ProtocolProfile> = {
  [N in Network]: ChainProtocolRegistry[N]["profile"] extends P ? N : never
}[Network];
export type MainnetNetwork = {
  [N in Network]: ChainProtocolRegistry[N]["environment"] extends "mainnet" ? N : never
}[Network];
export type UtxoNetwork = NetworkByFamily<"utxo">;
export type BitcoinNetwork = NetworkByProfile<"bitcoin-core-descriptor-psbt-v1">;
export type LitecoinNetwork = NetworkByProfile<"litecoin-core-legacy-psbt-v1">;
export type DogecoinNetwork = NetworkByProfile<"dogecoin-core-legacy-raw-v1">;
export type EvmNetwork = NetworkByFamily<"evm">;
export type TronNetwork = NetworkByFamily<"tron">;
export type SolanaNetwork = NetworkByFamily<"solana">;
export type AccountSweepNetwork = {
  [N in MainnetNetwork]: ChainProtocolRegistry[N]["family"] extends "utxo" ? never : N
}[MainnetNetwork];
export type NativeCoreRuntimeNetwork = {
  [N in Network]: ChainProtocolRegistry[N]["environment"] extends "mainnet"
    ? ChainProtocolRegistry[N]["profile"] extends NativeCoreProtocolProfile ? N : never
    : never
}[Network];
export type NativeAssetCode = ChainProtocolRegistry[Network]["nativeAsset"]["code"];
export type ChainFinalityMetadata = ChainProtocolRegistry[Network]["finality"];

export const networkIds = Object.freeze(Object.keys(chainProtocolRegistry) as Network[]);
export const networkSchema = z.enum(nonEmptyTuple(networkIds));
export const mainnetNetworks = filteredNetworks((descriptor) => descriptor.environment === "mainnet") as readonly [MainnetNetwork, ...MainnetNetwork[]];
export const utxoNetworks = filteredNetworks((descriptor) => descriptor.family === "utxo") as readonly [UtxoNetwork, ...UtxoNetwork[]];
export const bitcoinNetworks = filteredNetworks((descriptor) => descriptor.profile === "bitcoin-core-descriptor-psbt-v1") as readonly [BitcoinNetwork, ...BitcoinNetwork[]];
export const litecoinNetworks = filteredNetworks((descriptor) => descriptor.profile === "litecoin-core-legacy-psbt-v1") as readonly [LitecoinNetwork, ...LitecoinNetwork[]];
export const dogecoinNetworks = filteredNetworks((descriptor) => descriptor.profile === "dogecoin-core-legacy-raw-v1") as readonly [DogecoinNetwork, ...DogecoinNetwork[]];
export const nativeCoreRuntimeNetworks = filteredNetworks((descriptor) => (
  descriptor.environment === "mainnet"
  && descriptor.family === "utxo"
  && Object.hasOwn(nativeCoreRuntimeProfiles, descriptor.profile)
)) as readonly [NativeCoreRuntimeNetwork, ...NativeCoreRuntimeNetwork[]];
export const evmNetworks = filteredNetworks((descriptor) => descriptor.family === "evm") as readonly [EvmNetwork, ...EvmNetwork[]];
export const tronNetworks = filteredNetworks((descriptor) => descriptor.family === "tron") as readonly [TronNetwork, ...TronNetwork[]];
export const solanaNetworks = filteredNetworks((descriptor) => descriptor.family === "solana") as readonly [SolanaNetwork, ...SolanaNetwork[]];
export const accountSweepNetworks = filteredNetworks((descriptor) => (
  descriptor.environment === "mainnet" && descriptor.family !== "utxo"
)) as readonly [AccountSweepNetwork, ...AccountSweepNetwork[]];
const accountSweepNetworkSet = new Set<Network>(accountSweepNetworks);

const nativeDecimals = new Map<NativeAssetCode, number>();
for (const descriptor of Object.values(chainProtocolRegistry)) {
  nativeDecimals.set(descriptor.nativeAsset.code, descriptor.nativeAsset.decimals);
}
export const nativeAssetCodes = Object.freeze([...nativeDecimals.keys()]);
export const nativeAssetCodeSchema = z.enum(nonEmptyTuple(nativeAssetCodes));

function requiredProtocolForNetwork(network: Network): ChainProtocolRegistry[Network] {
  const descriptor = (chainProtocolRegistry as Readonly<Record<string, ChainProtocolRegistry[Network]>>)[network];
  if (descriptor === undefined) throw new Error(`CHAIN_NETWORK_UNKNOWN:${String(network)}`);
  return descriptor;
}

export function protocolForNetwork<N extends Network>(network: N): ChainProtocolRegistry[N] {
  return requiredProtocolForNetwork(network) as ChainProtocolRegistry[N];
}

export function isAccountSweepNetwork(network: Network): network is AccountSweepNetwork {
  return accountSweepNetworkSet.has(network);
}

export function nativeCoreRuntimeProfileForProtocolProfile(
  profile: ProtocolProfile,
): NativeCoreRuntimeProfile {
  const runtimeProfile = (nativeCoreRuntimeProfiles as Readonly<Partial<Record<ProtocolProfile, NativeCoreRuntimeProfile>>>)[profile];
  if (runtimeProfile === undefined) throw new Error(`CHAIN_NATIVE_CORE_RUNTIME_PROFILE_UNKNOWN:${profile}`);
  return runtimeProfile;
}

export function chainFamilyForNetwork(network: Network): ChainFamily {
  return requiredProtocolForNetwork(network).family;
}

export function isMainnetNetwork(network: Network): network is MainnetNetwork {
  return requiredProtocolForNetwork(network).environment === "mainnet";
}

export function isUtxoNetwork(network: Network): network is UtxoNetwork {
  return requiredProtocolForNetwork(network).family === "utxo";
}

export function isBitcoinNetwork(network: Network): network is BitcoinNetwork {
  return requiredProtocolForNetwork(network).profile === "bitcoin-core-descriptor-psbt-v1";
}

export function isLitecoinNetwork(network: Network): network is LitecoinNetwork {
  return requiredProtocolForNetwork(network).profile === "litecoin-core-legacy-psbt-v1";
}

export function isDogecoinNetwork(network: Network): network is DogecoinNetwork {
  return requiredProtocolForNetwork(network).profile === "dogecoin-core-legacy-raw-v1";
}

export function isEvmNetwork(network: Network): network is EvmNetwork {
  return requiredProtocolForNetwork(network).family === "evm";
}

export function isTronNetwork(network: Network): network is TronNetwork {
  return requiredProtocolForNetwork(network).family === "tron";
}

export function isSolanaNetwork(network: Network): network is SolanaNetwork {
  return requiredProtocolForNetwork(network).family === "solana";
}

export function nativeAssetForNetwork(network: Network): NativeAssetCode {
  return requiredProtocolForNetwork(network).nativeAsset.code;
}

export function expectedNativeAssetDecimals(assetCode: NativeAssetCode): number {
  const decimals = nativeDecimals.get(assetCode);
  if (decimals === undefined) throw new Error(`NATIVE_ASSET_UNKNOWN:${assetCode}`);
  return decimals;
}

export function networkDisplayName(network: Network): string {
  return requiredProtocolForNetwork(network).displayName;
}

export function networkFinalityMetadata(network: Network): ChainFinalityMetadata {
  return requiredProtocolForNetwork(network).finality;
}

export function networkAddressCodec(network: Network): AddressCodec {
  return requiredProtocolForNetwork(network).address.codec;
}

export function isBlockIdForProtocolNetwork(network: Network, blockId: string): boolean {
  return blockIdValidators[requiredProtocolForNetwork(network).transaction.blockIdCodec](blockId);
}

export function isAddressForProtocolNetwork(network: Network, address: string): boolean {
  return addressValidators[networkAddressCodec(network)](address);
}

type DescriptorPredicate = (descriptor: ProtocolDescriptor) => boolean;
function filteredNetworks(predicate: DescriptorPredicate): readonly [Network, ...Network[]] {
  return Object.freeze(nonEmptyTuple(networkIds.filter((network) => predicate(chainProtocolRegistry[network]))));
}

function nonEmptyTuple<T extends string>(values: readonly T[]): [T, ...T[]] {
  const [first, ...rest] = values;
  if (first === undefined) throw new Error("CHAIN_PROTOCOL_REGISTRY_EMPTY");
  return [first, ...rest];
}

function deepFreeze<T>(value: T): T {
  if (typeof value !== "object" || value === null || Object.isFrozen(value)) return value;
  for (const child of Object.values(value)) deepFreeze(child);
  return Object.freeze(value);
}

const bech32Characters = "qpzry9x8gf2tvdw0s3jn54khce6mua7l";
const bech32Generators = [0x3b6a57b2, 0x26508e6d, 0x1ea119fa, 0x3d4233dd, 0x2a1462b3] as const;
const base58Characters = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";

export const utxoAddressFormats = Object.freeze({
  "bitcoin-bech32-mainnet": {kind:"segwit",hrp:"bc",p2wpkhOnly:false,pubKeyHash:0,scriptHash:5,wif:128},
  "bitcoin-bech32-testnet": {kind:"segwit",hrp:"tb",p2wpkhOnly:false,pubKeyHash:111,scriptHash:196,wif:239},
  "bitcoin-bech32-regtest": {kind:"segwit",hrp:"bcrt",p2wpkhOnly:false,pubKeyHash:111,scriptHash:196,wif:239},
  "litecoin-bech32-p2wpkh-mainnet": {kind:"segwit",hrp:"ltc",p2wpkhOnly:true,pubKeyHash:48,scriptHash:50,wif:176},
  "dogecoin-base58-p2pkh-mainnet": {kind:"p2pkh",version:30,scriptHash:22,wif:158},
} as const);
export function utxoAddressFormat(codec:AddressCodec){
  if(!Object.hasOwn(utxoAddressFormats,codec))throw new Error('This address codec does not use a UTXO format.');
  return utxoAddressFormats[codec as keyof typeof utxoAddressFormats];
}
const utxoAddressValidators=Object.fromEntries(Object.entries(utxoAddressFormats).map(([codec,format])=>[codec,(address:string)=>format.kind==='segwit'?validSegwitAddress(address,format.hrp,format.p2wpkhOnly):validBase58CheckAddress(address,format.version)])) as Record<keyof typeof utxoAddressFormats,(address:string)=>boolean>;
const addressValidators = Object.freeze({
  ...utxoAddressValidators,
  "evm-hex20": (address: string) => /^0x[0-9a-fA-F]{40}$/u.test(address),
  "tron-base58check-mainnet": (address: string) => validBase58CheckAddress(address, 0x41),
  "solana-base58-pubkey": (address: string) => decodeBase58(address)?.length === 32,
} satisfies Readonly<Record<AddressCodec, (address: string) => boolean>>);

const blockIdValidators = Object.freeze({
  "hex64-lower": (value: string) => /^[0-9a-f]{64}$/u.test(value),
  "hex0x64-lower": (value: string) => /^0x[0-9a-f]{64}$/u.test(value),
  "tron-block-id-hex64": (value: string) => /^[0-9a-f]{64}$/u.test(value),
  "base58-case-sensitive": (value: string) => decodeBase58(value)?.length === 32,
} satisfies Readonly<Record<BlockIdCodec, (value: string) => boolean>>);

function validSegwitAddress(address: string, expectedHrp: string, p2wpkhOnly: boolean): boolean {
  if (address.length < 14 || address.length > 90 || address !== address.toLowerCase()) return false;
  const separator = address.lastIndexOf("1");
  if (separator < 1 || separator + 7 > address.length || address.slice(0, separator) !== expectedHrp) return false;
  const data: number[] = [];
  for (const character of address.slice(separator + 1)) {
    const value = bech32Characters.indexOf(character);
    if (value < 0) return false;
    data.push(value);
  }
  const witnessVersion = data[0];
  if (witnessVersion === undefined || witnessVersion > 16) return false;
  const checksum = bech32Polymod([...bech32HrpExpand(expectedHrp), ...data]);
  if ((witnessVersion === 0 && checksum !== 1) || (witnessVersion !== 0 && checksum !== 0x2bc830a3)) return false;
  const program = convertBits(data.slice(1, -6), 5, 8);
  if (program === null || program.length < 2 || program.length > 40) return false;
  if (witnessVersion === 0 && program.length !== 20 && program.length !== 32) return false;
  return !p2wpkhOnly || (witnessVersion === 0 && program.length === 20);
}

function bech32HrpExpand(hrp: string): number[] {
  return [
    ...[...hrp].map((character) => character.charCodeAt(0) >> 5),
    0,
    ...[...hrp].map((character) => character.charCodeAt(0) & 31),
  ];
}

function bech32Polymod(values: readonly number[]): number {
  let checksum = 1;
  for (const value of values) {
    const high = checksum >>> 25;
    checksum = (((checksum & 0x1ffffff) << 5) ^ value) >>> 0;
    for (let index = 0; index < bech32Generators.length; index += 1) {
      if (((high >>> index) & 1) !== 0) checksum = (checksum ^ bech32Generators[index]!) >>> 0;
    }
  }
  return checksum >>> 0;
}

function convertBits(values: readonly number[], fromBits: number, toBits: number): Uint8Array | null {
  let accumulator = 0;
  let bitCount = 0;
  const result: number[] = [];
  const maximumOutput = (1 << toBits) - 1;
  for (const value of values) {
    if (value < 0 || value >>> fromBits !== 0) return null;
    accumulator = ((accumulator << fromBits) | value) >>> 0;
    bitCount += fromBits;
    while (bitCount >= toBits) {
      bitCount -= toBits;
      result.push((accumulator >>> bitCount) & maximumOutput);
    }
  }
  if (bitCount >= fromBits || ((accumulator << (toBits - bitCount)) & maximumOutput) !== 0) return null;
  return Uint8Array.from(result);
}

function validBase58CheckAddress(address: string, version: number): boolean {
  const decoded = decodeBase58(address);
  if (decoded === null || decoded.length !== 25 || decoded[0] !== version) return false;
  const checksum = sha256Bytes(sha256Bytes(decoded.subarray(0, 21))).subarray(0, 4);
  for (let index = 0; index < checksum.length; index += 1) {
    if (decoded[21 + index] !== checksum[index]) return false;
  }
  return true;
}

function decodeBase58(value: string): Uint8Array | null {
  if (value.length === 0) return null;
  let number = 0n;
  for (const character of value) {
    const digit = base58Characters.indexOf(character);
    if (digit < 0) return null;
    number = number * 58n + BigInt(digit);
  }
  const reversed: number[] = [];
  while (number > 0n) {
    reversed.push(Number(number & 0xffn));
    number >>= 8n;
  }
  let zeroes = 0;
  while (zeroes < value.length && value[zeroes] === "1") zeroes += 1;
  const decoded = new Uint8Array(zeroes + reversed.length);
  decoded.set(reversed.reverse(), zeroes);
  return decoded;
}

const sha256Constants = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

function sha256Bytes(input: Uint8Array): Uint8Array {
  const paddingLength = (64 - ((input.length + 1 + 8) % 64)) % 64;
  const padded = new Uint8Array(input.length + 1 + paddingLength + 8);
  padded.set(input);
  padded[input.length] = 0x80;
  let bitLength = BigInt(input.length) * 8n;
  for (let index = 0; index < 8; index += 1) {
    padded[padded.length - 1 - index] = Number(bitLength & 0xffn);
    bitLength >>= 8n;
  }
  const state = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
    0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);
  const words = new Uint32Array(64);
  const view = new DataView(padded.buffer, padded.byteOffset, padded.byteLength);
  for (let offset = 0; offset < padded.length; offset += 64) {
    for (let index = 0; index < 16; index += 1) words[index] = view.getUint32(offset + index * 4, false);
    for (let index = 16; index < 64; index += 1) {
      const word15 = words[index - 15]!;
      const word2 = words[index - 2]!;
      const sigma0 = rotateRight(word15, 7) ^ rotateRight(word15, 18) ^ (word15 >>> 3);
      const sigma1 = rotateRight(word2, 17) ^ rotateRight(word2, 19) ^ (word2 >>> 10);
      words[index] = (words[index - 16]! + sigma0 + words[index - 7]! + sigma1) >>> 0;
    }
    let a = state[0]!; let b = state[1]!; let c = state[2]!; let d = state[3]!;
    let e = state[4]!; let f = state[5]!; let g = state[6]!; let h = state[7]!;
    for (let index = 0; index < 64; index += 1) {
      const sum1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25);
      const choice = (e & f) ^ (~e & g);
      const temporary1 = (h + sum1 + choice + sha256Constants[index]! + words[index]!) >>> 0;
      const sum0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);
      const majority = (a & b) ^ (a & c) ^ (b & c);
      const temporary2 = (sum0 + majority) >>> 0;
      h = g; g = f; f = e; e = (d + temporary1) >>> 0;
      d = c; c = b; b = a; a = (temporary1 + temporary2) >>> 0;
    }
    state[0] = (state[0]! + a) >>> 0; state[1] = (state[1]! + b) >>> 0;
    state[2] = (state[2]! + c) >>> 0; state[3] = (state[3]! + d) >>> 0;
    state[4] = (state[4]! + e) >>> 0; state[5] = (state[5]! + f) >>> 0;
    state[6] = (state[6]! + g) >>> 0; state[7] = (state[7]! + h) >>> 0;
  }
  const output = new Uint8Array(32);
  const outputView = new DataView(output.buffer);
  for (let index = 0; index < state.length; index += 1) outputView.setUint32(index * 4, state[index]!, false);
  return output;
}

function rotateRight(value: number, bits: number): number {
  return ((value >>> bits) | (value << (32 - bits))) >>> 0;
}

export const chainProtocolCatalogProjection = deepFreeze({
  version: 1,
  networks: chainProtocolRegistry,
} as const);
